import React, { useState } from "react";

import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { useMediaEncoding } from "../../hooks/useMediaEncoding";
import { isChildItem } from "../../utils/typePredicate";
import ChildContentHeader from "../contentTabs/media/ChildContentHeader";
import EncodingConfigPanel from "../encoding/EncodingConfigPanel";
import EncodingOperationsDrawer from "../encoding/encodingOperationDrawer/EncodingOperationsDrawer";
import EncodingPanelSimple from "../encoding/EncodingPanelSimple";
import OutputStatusPanel from "../encoding/OutputStatusPanel";
import PreviewPlayerPanel from "../encoding/PreviewPlayerPanel";
import SourceMediaPanel from "../encoding/sourceMediaPanel/SourceMediaPanel";

import type { GetMediaResponse, MediaChildren } from "../../api/MediaAPI.types";

const VIEW_MODE_STORAGE_KEY = "encoding-view-mode";

function toggleButtonClass(active: boolean): string {
  return classnames(
    "px-[0.78125rem] py-1.25 text-[0.78125rem] font-medium transition-colors focus:outline-none",
    active
      ? "bg-primary text-primary-foreground"
      : "bg-card text-muted-foreground hover:bg-muted",
  );
}

export default function ContentEncoder({
  item,
}: {
  item: GetMediaResponse | MediaChildren;
}): React.JSX.Element {
  const { t } = useTranslation("editor-content");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // eslint-disable-next-line react/hook-use-state
  const [isSimpleMode, setIsSimpleModeState] = useState<boolean>(() => {
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === null ? true : stored === "simple";
  });

  const setIsSimpleMode = (value: boolean): void => {
    window.localStorage.setItem(
      VIEW_MODE_STORAGE_KEY,
      value ? "simple" : "detailed",
    );
    setIsSimpleModeState(value);
  };

  const {
    sourceMedias,
    streams,
    fastAsset,
    isEncoding,
    isReadyToPlay,
    isLoading,
    isUploading,
    isQueued,
    uploadProgress,
    encodingProfiles,
    isProfilesLoading,
    onAddFile,
    onDeleteSourceMedia,
    onDeleteStream,
    onUpdateStream,
    onDeleteFastAsset,
    onStartEncoding,
    isStartingEncoding,
    onUploadFromUrl,
    currentFile,
    isUploadError,
    isUploadBlocked,
    onResetUpload,
    uploadedMimeTypeMap,
  } = useMediaEncoding(item.uuid || "");

  const isAudioOnly = "audio_only" in item ? item.audio_only : false;

  const onViewDetails = (): void => setIsDrawerOpen(true);

  return (
    <div className="w-full">
      {isChildItem(item) && <ChildContentHeader item={item} />}

      <div className="mt-[0.9375rem] flex items-center justify-end gap-[0.46875rem]">
        <span className="text-muted-foreground text-[0.78125rem] font-medium">
          {t("viewModeLabel")}
        </span>
        <div className="border-border inline-flex overflow-hidden rounded-lg border">
          <button
            className={toggleButtonClass(isSimpleMode)}
            onClick={(): void => setIsSimpleMode(true)}
            type="button"
          >
            {t("viewModeSimple")}
          </button>
          <button
            onClick={(): void => setIsSimpleMode(false)}
            type="button"
            className={classnames(
              toggleButtonClass(!isSimpleMode),
              "border-border border-l",
            )}
          >
            {t("viewModeDetailed")}
          </button>
        </div>
      </div>

      <div
        className={classnames(
          "mt-2.5 grid grid-cols-1 gap-6.25 rounded-lg min-[1100px]:grid-cols-2",
          isSimpleMode
            ? "[grid-template-rows:repeat(4,minmax(300px,475px))] min-[1100px]:[grid-template-rows:repeat(2,minmax(275px,415px))]"
            : "[grid-template-rows:repeat(4,minmax(350px,475px))] min-[1100px]:[grid-template-rows:repeat(2,minmax(350px,475px))]",
        )}
      >
        <div>
          <SourceMediaPanel
            currentFile={currentFile}
            isAudioOnly={isAudioOnly}
            isEncoding={isEncoding}
            isLoading={isLoading}
            isQueued={isQueued}
            isSimpleMode={isSimpleMode}
            isUploadBlocked={isUploadBlocked}
            isUploadError={isUploadError}
            isUploading={isUploading}
            onAddFile={onAddFile}
            onDeleteSourceMedia={onDeleteSourceMedia}
            onResetUpload={onResetUpload}
            onUploadFromUrl={onUploadFromUrl}
            sourceMedias={sourceMedias}
            uploadedMimeTypeMap={uploadedMimeTypeMap}
            uploadProgress={uploadProgress}
          />
        </div>
        <PreviewPlayerPanel isReadyToPlay={isReadyToPlay} item={item} />
        {isSimpleMode ? (
          <div className="min-[1100px]:col-span-2">
            <EncodingPanelSimple
              encodingProfiles={encodingProfiles}
              fastAsset={fastAsset}
              isEncoding={isEncoding}
              isProfilesLoading={isProfilesLoading}
              isStartingEncoding={isStartingEncoding}
              onStartEncoding={onStartEncoding}
              onViewDetails={onViewDetails}
              sourceMedias={sourceMedias}
              streams={streams}
            />
          </div>
        ) : (
          <React.Fragment>
            <EncodingConfigPanel
              encodingProfiles={encodingProfiles}
              fastAsset={fastAsset}
              isEncoding={isEncoding}
              isProfilesLoading={isProfilesLoading}
              isStartingEncoding={isStartingEncoding}
              onStartEncoding={onStartEncoding}
              sourceMedias={sourceMedias}
            />
            <OutputStatusPanel
              fastAsset={fastAsset}
              onViewDetails={onViewDetails}
              streams={streams}
            />
          </React.Fragment>
        )}
      </div>

      <EncodingOperationsDrawer
        fastAsset={fastAsset}
        isOpen={isDrawerOpen}
        onClose={(): void => setIsDrawerOpen(false)}
        onDeleteFastAsset={onDeleteFastAsset}
        onDeleteStream={onDeleteStream}
        onUpdateStream={onUpdateStream}
        streams={streams}
      />
    </div>
  );
}
